import React from 'react';
import { 
  ArrowRight, 
  Search, 
  Shield,
  FileText,
  BarChart,
  Zap,
  Settings,
  CheckCircle,
  Clock,
  Target,
  Server,
  Database,
  Lock,
  LineChart,
  Network,
  Brain,
  Users,
  BookOpen,
  Building2,
  FileCheck // Ajout de l'icône FileCheck manquante
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Amoa = () => {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: Target,
      title: "Vision Stratégique",
      description: "Alignement des projets avec vos objectifs business",
      points: [
        "Analyse des besoins métier",
        "Identification des opportunités",
        "Définition de la roadmap",
        "Alignement stratégique"
      ]
    },
    {
      icon: Brain,
      title: "Expertise Métier",
      description: "Compréhension approfondie du secteur financier",
      points: [
        "Connaissance réglementaire",
        "Best practices sectorielles",
        "Expertise fonctionnelle",
        "Veille métier continue"
      ]
    },
    {
      icon: Zap,
      title: "Performance & ROI",
      description: "Optimisation des investissements et de la valeur",
      points: [
        "Analyse coûts/bénéfices",
        "Priorisation des initiatives",
        "Mesure de la performance",
        "Suivi des KPIs"
      ]
    }
  ];

  const process = [
    {
      icon: FileCheck,
      title: "Cadrage & Étude",
      description: "Définition précise du périmètre et des objectifs",
      duration: "2-4 semaines",
      deliverables: [
        "Analyse de l'existant",
        "Expression des besoins",
        "Étude de faisabilité",
        "Business case"
      ]
    },
    {
      icon: Users,
      title: "PMO & Pilotage",
      description: "Coordination des équipes et suivi du projet",
      duration: "Durée du projet",
      deliverables: [
        "Planning détaillé",
        "Suivi des risques",
        "Reporting régulier",
        "Pilotage budgétaire"
      ]
    },
    {
      icon: Settings,
      title: "Accompagnement",
      description: "Support continu et conduite du changement",
      duration: "Post-projet",
      deliverables: [
        "Formation des équipes",
        "Documentation",
        "Support utilisateurs",
        "Retour d'expérience"
      ]
    }
  ];

  const expertise = [
    {
      icon: Building2,
      title: "Core Banking",
      description: "Transformation des systèmes bancaires"
    },
    {
      icon: Brain,
      title: "Finance de Marché",
      description: "Optimisation des plateformes de trading"
    },
    {
      icon: Shield,
      title: "Réglementaire",
      description: "Mise en conformité DSP2, RGPD"
    },
    {
      icon: Zap,
      title: "Innovation",
      description: "Intégration nouvelles technologies"
    }
  ];

  return (
    <div className="min-h-screen text-white pt-20 md:pt-32">
      {/* Background effects */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(1,125,126,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center_left,rgba(0,169,157,0.1),transparent_50%)]" />
      </div>

      {/* Hero Section */}
      <div className="container mx-auto px-4 mb-8 md:mb-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block px-4 py-2 rounded-full bg-[#017D7E]/10 text-[#017D7E] mb-6 text-sm">
            AMOA & Conseil
          </div>
          
          <h1 className="text-3xl md:text-5xl font-bold mb-6">
            Transformez Vos Ambitions
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]">
              En Projets Réussis
            </span>
          </h1>

          <p className="text-lg md:text-xl text-white/70 mb-8">
            Un accompagnement expert pour piloter vos projets stratégiques et 
            garantir leur succès, de la vision à la réalisation.
          </p>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-12 md:py-24 bg-white/5">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Les Bénéfices</h2>
              <p className="text-lg text-white/70">
                Une approche qui combine expertise métier et méthodologie éprouvée
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-white/5 rounded-xl p-8 border border-white/10
                                       hover:border-green-500/50 transition-all duration-300">
                  <div className="p-3 rounded-lg bg-[#017D7E]/10 w-fit mb-6">
                    <benefit.icon className="w-6 h-6 text-[#017D7E]" />
                  </div>

                  <h3 className="text-xl font-bold mb-4">{benefit.title}</h3>
                  <p className="text-white/70 mb-6">{benefit.description}</p>

                  <ul className="space-y-3">
                    {benefit.points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-white/70">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#017D7E] mt-2" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Process Section */}
      <div className="py-12 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Notre
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]">
                  {" "}Méthodologie
                </span>
              </h2>
              <p className="text-lg text-white/70">
                Une approche structurée en 3 phases pour des résultats garantis
              </p>
            </div>

            <div className="space-y-8">
              {process.map((step, index) => (
                <div key={index} className="bg-white/5 rounded-xl p-6 md:p-8 border border-white/10
                                       hover:border-green-500/50 transition-all duration-300">
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-[#017D7E]/10">
                        <step.icon className="w-6 h-6 text-[#017D7E]" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                        <p className="text-white/70">{step.description}</p>
                      </div>
                    </div>

                    <div className="md:ml-auto flex items-center">
                      <div className="px-4 py-2 rounded-full bg-[#017D7E]/10 text-[#017D7E] text-sm">
                        {step.duration}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {step.deliverables.map((deliverable, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-white/70">
                        <CheckCircle className="w-5 h-5 text-[#017D7E]" />
                        {deliverable}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Expertise Section */}
      <div className="py-12 md:py-24 bg-white/5">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Domaines d'Expertise</h2>
              <p className="text-lg text-white/70">
                Une expertise pointue des métiers de la finance
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {expertise.map((domain, index) => (
                <div key={index} className="bg-white/5 rounded-xl p-6 text-center border border-white/10
                                       hover:border-green-500/50 transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl bg-[#017D7E]/10 flex items-center justify-center mx-auto mb-4">
                    <domain.icon className="w-6 h-6 text-[#017D7E]" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">{domain.title}</h3>
                  <p className="text-sm text-white/70">{domain.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-12 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Prêt à Démarrer Votre Projet ?
            </h2>
            <p className="text-lg text-white/70 mb-8">
              Nos experts sont à votre disposition pour échanger sur vos besoins
              et vous accompagner dans votre transformation.
            </p>
            
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/contact')}
                className="px-8 py-4 rounded-lg bg-gradient-to-r from-green-500 to-[#017D7E] 
                          hover:from-green-600 hover:to-emerald-600 text-white font-medium transition-all">
                Nous contacter
              </button>
              
              <button
                onClick={() => navigate('/services')}
                className="px-8 py-4 rounded-lg border border-green-500/50 
                          hover:bg-[#017D7E]/10 text-white font-medium transition-all">
                Autres services
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Amoa;